import React, { useMemo } from 'react';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { ISammanfattningRows } from './EkonomiskAnalysSammanfattning';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { getCurrencyFormatterParams } from '../../Utils/paramUtils';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  LEVEL_VERKSAMHETSNIVA,
} from '../../Const/EkonomiskAnalysConsts';
import {
  hasKoddelCodeTwiceInKodelRegex,
  shouldHaveGreenValue,
  trimVerksamhetsniva,
  getAnalysTypeLabel,
} from '../../Utils/detaljanalysUtils';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { roundNumberToDecimals } from '../../../../../Leverantor/ObjektVyer/FakturaDel/Components/Kontering/Utils/KonteringUtils';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { getMonthText } from '../../../../../../../Utils/dateUtils';
import { EDControlledExpansionPanel } from '../../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/system';
import EkonomiskAnalysExpansionLabel from './Components/EkonomiskAnalysSammanfattningCharts/Components/EkonomiskAnalysExpansionLabel';

export interface IProps {
  analysLevel: AnalysLevelEnum;
  analysType: AnalysTypeEnum;
  analysName: string;
  expandedDetails: boolean;
  dates: IEkonomiskAnalysFilledDates;
  denomination: number;
  financialUnit?: string;
  organisationName?: string;
  rubrikNiva?: string;
  selectedRowHeader?: string;
  setExpandedDetails: (value: boolean) => void;
  showChangedCodePrivilegeText: boolean;
  tableRows: ISammanfattningRows[] | undefined;
}
const currencyFormatter = findFormatter('TEDFormatCurr');
const percentFormatter = findFormatter('TEDFormatIntAsPercent');

export const SammanfattningDetails: React.FC<IProps> = (props: IProps) => {
  const currencyFormatterParams = useMemo(() => {
    return getCurrencyFormatterParams(
      { fieldInfo: [] },
      props.denomination,
      0,
      'Y'
    );
  }, [props.denomination]);

  const currentMonth = getMonthText(props.dates.from.month);

  const currentYear = props.dates.from.year;

  const theme = useTheme();

  const { sammanfattningEyebrow, sammanfattningTitle } = useMemo(() => {
    if (props.analysLevel === AnalysLevelEnum.UNSET) {
      return {
        sammanfattningEyebrow: 'Organisation',
        sammanfattningTitle: props.organisationName ?? '',
      };
    }
    if (
      (props.analysLevel === AnalysLevelEnum.MIDDLE ||
        props.analysLevel === AnalysLevelEnum.LAST) &&
      props.selectedRowHeader &&
      props.rubrikNiva
    ) {
      const isVerksamhetsNiva =
        props.rubrikNiva === LEVEL_VERKSAMHETSNIVA &&
        hasKoddelCodeTwiceInKodelRegex.test(props.selectedRowHeader);
      const headerValue = isVerksamhetsNiva
        ? trimVerksamhetsniva(props.selectedRowHeader)
        : props.selectedRowHeader;
      return {
        sammanfattningEyebrow: props.rubrikNiva.toUpperCase(),
        sammanfattningTitle: headerValue,
      };
    }
    return {
      sammanfattningEyebrow: '',
      sammanfattningTitle: 'Felaktig analysnivå eller rubrik',
    };
  }, [
    props.analysLevel,
    props.organisationName,
    props.rubrikNiva,
    props.selectedRowHeader,
  ]);

  const sumUtgaendeVardePeriod = useMemo(
    () =>
      props.tableRows?.reduce(
        (sum, row) => sum + (row.utgaendeVarde || 0),
        0
      ) || 0,
    [props.tableRows]
  );

  const sumBudgetRiktvarde = useMemo(
    () =>
      props.tableRows?.reduce(
        (sum, row) => sum + (row.budgetRiktvarde || 0),
        0
      ) || 0,
    [props.tableRows]
  );

  const sumHelarsprognos = useMemo(
    () =>
      props.tableRows?.reduce(
        (sum, row) => sum + (row.helarsPrognos || 0),
        0
      ) || 0,
    [props.tableRows]
  );

  const sumBudgetArVarde = useMemo(
    () =>
      props.tableRows?.reduce(
        (sum, row) => sum + (row.budgetArVarde || 0),
        0
      ) || 0,
    [props.tableRows]
  );

  const sumAvvikelseBudgetRiktvarde = useMemo(
    () => roundNumberToDecimals(sumUtgaendeVardePeriod - sumBudgetRiktvarde, 0),
    [sumUtgaendeVardePeriod, sumBudgetRiktvarde]
  );

  const sumAvvikelseProcentBudgetRiktvarde = useMemo(() => {
    //Avrunda sumBudgetRiktvarde med två decimaler innan uträkning av procent för att undvika att få väldigt stora procentsatser när sumBudgetRiktvarde är väldigt lågt
    const roundedSumBudgetRiktvarde = roundNumberToDecimals(
      sumBudgetRiktvarde,
      2
    );

    const percentage =
      roundedSumBudgetRiktvarde && roundedSumBudgetRiktvarde !== 0
        ? Math.abs(
            sumAvvikelseBudgetRiktvarde / roundedSumBudgetRiktvarde
          ).toFixed(4)
        : '0';
    return percentage;
  }, [sumAvvikelseBudgetRiktvarde, sumBudgetRiktvarde]);

  const isKostnadsbudget =
    props.analysType === AnalysTypeEnum.RESULTATUPPFOLJNING &&
    sumAvvikelseBudgetRiktvarde < 0;

  return (
    <EDControlledExpansionPanel
      headerColor={theme.customProperties.MenuList.baseBg}
      borderVisible
      detailsBackgroundColor={
        theme.customProperties.customDetaljanalys.parchment
      }
      expanded={props.expandedDetails}
      expandIconColor="primary"
      label={
        <EkonomiskAnalysExpansionLabel
          labelText="Sammanfattning"
          subLabelText={getAnalysTypeLabel(props.analysType)}
          iconType="task_list"
          iconVerticalAlign="sub"
        />
      }
      onChange={(_, newValue) => props.setExpandedDetails(newValue)}
      isCompact
      disableDetailsPadding
    >
      <EDBox
        sx={theme => ({
          backgroundColor: theme.customProperties.customDetaljanalys.canvas,
          padding: '20px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          '@media print': {
            padding: '20px',
            pageBreakInside: 'avoid',
          },
        })}
      >
        <EDBox sx={{ width: '100%', mb: 2, textAlign: 'center' }}>
          {props.showChangedCodePrivilegeText && (
            <EDTypography
              variant="body2"
              sx={theme => ({
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                mb: 0.5,
              })}
            >
              Filtrerat enligt kodbehörighet
            </EDTypography>
          )}
          <EDTypography
            variant="overline"
            sx={theme => ({
              color: theme.palette.text.secondary,
              letterSpacing: '0.08em',
              fontSize: '11px',
              lineHeight: 1.2,
              display: 'block',
            })}
          >
            {sammanfattningEyebrow}
          </EDTypography>
          <EDTypography
            variant="h5"
            sx={{
              fontWeight: 600,
              mt: 0.5,
              lineHeight: 1.2,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <span>{sammanfattningTitle}</span>
            <EDTypography
              component="span"
              variant="body1"
              sx={theme => ({
                color: theme.palette.text.secondary,
                fontWeight: 400,
                '&::before': {
                  content: '"·"',
                  display: 'inline-block',
                  marginRight: theme.spacing(1),
                  opacity: 0.6,
                },
              })}
            >
              {currentMonth} {currentYear}
            </EDTypography>
          </EDTypography>
        </EDBox>

        <EDBox
          sx={{
            display: 'flex',
            gap: 3,
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            '@media print': {
              maxWidth: '100%',
              gap: '4px',
              '& > div:empty': {
                display: 'none',
              },
              '& > div': {
                gap: '4px !important',
              },
              '& > div > div': {
                padding: '4px 8px !important',
                gap: '2px !important',
              },
              '& .MuiTypography-h6': {
                fontSize: '0.7rem !important',
              },
              '& .MuiTypography-caption': {
                fontSize: '0.5rem !important',
                letterSpacing: '0.02em !important',
              },
            },
          }}
        >
          <EDBox sx={{ display: 'flex', gap: 2, flex: '1 1 0', minWidth: 0 }}>
            <EDBox
              sx={theme => ({
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Utgående värde {currentMonth}
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  '@media print': { whiteSpace: 'nowrap' },
                }}
              >
                {sumUtgaendeVardePeriod !== 0
                  ? `${currencyFormatter.displayer.display(
                      sumUtgaendeVardePeriod,
                      true,
                      currencyFormatterParams
                    )} ${props.financialUnit}`
                  : null}
              </EDTypography>
            </EDBox>
            <EDBox
              sx={theme => ({
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Budget riktvärde {currentMonth}
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  '@media print': { whiteSpace: 'nowrap' },
                }}
              >
                {sumBudgetRiktvarde !== 0
                  ? `${currencyFormatter.displayer.display(
                      sumBudgetRiktvarde,
                      true,
                      currencyFormatterParams
                    )} ${props.financialUnit}`
                  : '-'}
              </EDTypography>
            </EDBox>
          </EDBox>

          <EDBox
            sx={theme => ({
              borderLeft: `1px solid ${theme.palette.divider}`,
              alignSelf: 'stretch',
            })}
          />

          <EDBox sx={{ display: 'flex', gap: 2, flex: '1 1 0', minWidth: 0 }}>
            <EDBox
              sx={theme => ({
                backgroundColor: alpha(
                  shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType
                  )
                    ? theme.palette.primary.light
                    : theme.customProperties.Legend.red.full,
                  0.1
                ),
                border: `1px solid ${alpha(
                  shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType
                  )
                    ? theme.customProperties.Legend.darkGreen.full
                    : theme.customProperties.Legend.red.full,
                  0.15
                )}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Avvikelse budget riktvärde
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={theme => ({
                  fontWeight: 700,
                  color: shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType
                  )
                    ? theme.customProperties.Legend.darkGreen.full
                    : theme.customProperties.Legend.red.full,
                  '@media print': { whiteSpace: 'nowrap' },
                })}
              >
                {sumAvvikelseBudgetRiktvarde != 0
                  ? `${currencyFormatter.displayer.display(
                      sumAvvikelseBudgetRiktvarde,
                      true,
                      currencyFormatterParams
                    )} ${props.financialUnit}`
                  : '-'}
              </EDTypography>
            </EDBox>
            <EDBox
              sx={theme => ({
                backgroundColor: alpha(
                  shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType,
                    isKostnadsbudget
                  )
                    ? theme.palette.primary.light
                    : theme.customProperties.Legend.red.full,
                  0.1
                ),
                border: `1px solid ${alpha(
                  shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType,
                    isKostnadsbudget
                  )
                    ? theme.customProperties.Legend.darkGreen.full
                    : theme.customProperties.Legend.red.full,
                  0.15
                )}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Avvikelse budget riktvärde %
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={theme => ({
                  fontWeight: 700,
                  color: shouldHaveGreenValue(
                    sumAvvikelseBudgetRiktvarde,
                    props.analysType,
                    isKostnadsbudget
                  )
                    ? theme.customProperties.Legend.darkGreen.full
                    : theme.customProperties.Legend.red.full,
                  '@media print': { whiteSpace: 'nowrap' },
                })}
              >
                {sumAvvikelseProcentBudgetRiktvarde !== '0'
                  ? percentFormatter.displayer.display(
                      sumAvvikelseProcentBudgetRiktvarde,
                      true
                    )
                  : '-'}
              </EDTypography>
            </EDBox>
          </EDBox>

          <EDBox
            sx={theme => ({
              borderLeft: `1px solid ${theme.palette.divider}`,
              alignSelf: 'stretch',
            })}
          />

          <EDBox sx={{ display: 'flex', gap: 2, flex: '1 1 0', minWidth: 0 }}>
            <EDBox
              sx={theme => ({
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                Helårsprognos
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  '@media print': { whiteSpace: 'nowrap' },
                }}
              >
                {sumHelarsprognos !== 0
                  ? `${currencyFormatter.displayer.display(
                      sumHelarsprognos,
                      true,
                      currencyFormatterParams
                    )} ${props.financialUnit}`
                  : '-'}
              </EDTypography>
            </EDBox>
            <EDBox
              sx={theme => ({
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2.5, 3),
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 1 0',
                minWidth: 0,
              })}
            >
              <EDTypography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {`Budget ${currentYear}`}
              </EDTypography>
              <EDTypography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  '@media print': { whiteSpace: 'nowrap' },
                }}
              >
                {sumBudgetArVarde !== 0
                  ? `${currencyFormatter.displayer.display(
                      sumBudgetArVarde,
                      true,
                      currencyFormatterParams
                    )} ${props.financialUnit}`
                  : '-'}
              </EDTypography>
            </EDBox>
          </EDBox>
        </EDBox>
      </EDBox>
    </EDControlledExpansionPanel>
  );
};

export default SammanfattningDetails;
